// Standard card aspect ratio (3.375" x 2.125")
const OUTPUT_WIDTH = 600;
const OUTPUT_HEIGHT = 380;

/**
 * Pure JS perspective correction. Warps the card region defined by `corners`
 * into a flat OUTPUT_WIDTH x OUTPUT_HEIGHT rectangle.
 */
export function correctPerspective(sourceCanvas, corners) {
  const dst = document.createElement('canvas');
  dst.width = OUTPUT_WIDTH;
  dst.height = OUTPUT_HEIGHT;
  const dstCtx = dst.getContext('2d');
  if (!dstCtx) return dst;

  const srcCtx = sourceCanvas.getContext('2d');
  if (!srcCtx) return dst;

  const srcData = srcCtx.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
  const dstData = dstCtx.createImageData(OUTPUT_WIDTH, OUTPUT_HEIGHT);

  const src = [
    [corners[0].x, corners[0].y],
    [corners[1].x, corners[1].y],
    [corners[2].x, corners[2].y],
    [corners[3].x, corners[3].y],
  ];

  const dstPts = [
    [0, 0],
    [OUTPUT_WIDTH, 0],
    [OUTPUT_WIDTH, OUTPUT_HEIGHT],
    [0, OUTPUT_HEIGHT],
  ];

  const H = computeHomography(dstPts, src);
  if (!H) return dst;

  const srcW = sourceCanvas.width;
  const srcH = sourceCanvas.height;
  const srcPixels = srcData.data;
  const dstPixels = dstData.data;

  for (let y = 0; y < OUTPUT_HEIGHT; y++) {
    for (let x = 0; x < OUTPUT_WIDTH; x++) {
      const w = H[6] * x + H[7] * y + H[8];
      if (Math.abs(w) < 1e-10) continue;

      const srcX = (H[0] * x + H[1] * y + H[2]) / w;
      const srcY = (H[3] * x + H[4] * y + H[5]) / w;

      const x0 = Math.floor(srcX);
      const y0 = Math.floor(srcY);
      const x1 = x0 + 1;
      const y1 = y0 + 1;

      if (x0 < 0 || y0 < 0 || x1 >= srcW || y1 >= srcH) continue;

      const fx = srcX - x0;
      const fy = srcY - y0;

      const dstIdx = (y * OUTPUT_WIDTH + x) * 4;
      const i00 = (y0 * srcW + x0) * 4;
      const i10 = (y0 * srcW + x1) * 4;
      const i01 = (y1 * srcW + x0) * 4;
      const i11 = (y1 * srcW + x1) * 4;

      for (let c = 0; c < 4; c++) {
        dstPixels[dstIdx + c] =
          (1 - fx) * (1 - fy) * srcPixels[i00 + c] +
          fx * (1 - fy) * srcPixels[i10 + c] +
          (1 - fx) * fy * srcPixels[i01 + c] +
          fx * fy * srcPixels[i11 + c];
      }
    }
  }

  dstCtx.putImageData(dstData, 0, 0);
  return dst;
}

function computeHomography(src, dst) {
  const A = [];
  const b = [];

  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i];
    const [xp, yp] = dst[i];

    A.push([x, y, 1, 0, 0, 0, -x * xp, -y * xp]);
    b.push(xp);

    A.push([0, 0, 0, x, y, 1, -x * yp, -y * yp]);
    b.push(yp);
  }

  const h = solveLinearSystem(A, b);
  if (!h) return null;

  return [...h, 1];
}

function solveLinearSystem(A, b) {
  const n = b.length;
  const aug = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    let maxRow = col;
    let maxVal = Math.abs(aug[col][col]);
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(aug[row][col]) > maxVal) {
        maxVal = Math.abs(aug[row][col]);
        maxRow = row;
      }
    }
    if (maxVal < 1e-10) return null;

    if (maxRow !== col) {
      [aug[col], aug[maxRow]] = [aug[maxRow], aug[col]];
    }

    for (let row = col + 1; row < n; row++) {
      const factor = aug[row][col] / aug[col][col];
      for (let j = col; j <= n; j++) {
        aug[row][j] -= factor * aug[col][j];
      }
    }
  }

  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let sum = aug[i][n];
    for (let j = i + 1; j < n; j++) {
      sum -= aug[i][j] * x[j];
    }
    if (Math.abs(aug[i][i]) < 1e-10) return null;
    x[i] = sum / aug[i][i];
  }

  return x;
}
